import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const DEFAULT_QUESTIONS = [
  { key: "energy", label: "Como está sua energia?", description: "0 = exausto(a) · 10 = energia ótima", min: 0, max: 10 },
  { key: "sleep", label: "Como está seu sono?", description: "0 = péssimo · 10 = restaurador", min: 0, max: 10 },
  { key: "intestine", label: "Como está seu intestino?", description: "0 = muito ruim · 10 = regulado", min: 0, max: 10 },
  { key: "adherence", label: "Qual sua adesão ao protocolo?", description: "0 = não seguiu · 10 = seguiu 100%", min: 0, max: 10 },
];

export default function CheckinPage() {
  const [questions, setQuestions] = useState(DEFAULT_QUESTIONS);
  const [answers, setAnswers] = useState(() => Object.fromEntries(DEFAULT_QUESTIONS.map((q) => [q.key, 5])));
  const [sideEffects, setSideEffects] = useState("");
  const [history, setHistory] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/checkin/weekly");
      setHistory(data);
    } catch (_) { /* noop */ }
  };

  const loadTemplate = async () => {
    try {
      const { data } = await api.get("/protocols/checkin-template");
      const qs = (data?.questions && data.questions.length > 0) ? data.questions : DEFAULT_QUESTIONS;
      setQuestions(qs);
      setAnswers(Object.fromEntries(qs.map((q) => [q.key, 5])));
    } catch (_) {
      setQuestions(DEFAULT_QUESTIONS);
      setAnswers(Object.fromEntries(DEFAULT_QUESTIONS.map((q) => [q.key, 5])));
    }
  };

  useEffect(() => { loadHistory(); loadTemplate(); }, []);

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = { answers, side_effects: sideEffects };
      // Also send legacy fields when the corresponding key is present (for backward-compat)
      for (const k of ["energy", "sleep", "intestine", "adherence"]) {
        if (answers[k] !== undefined) payload[k] = Number(answers[k]);
      }
      const { data } = await api.post("/checkin/weekly", payload);
      setLastResult(data);
      toast.success(`Check-in registrado · Score ${data.score}`);
      await loadHistory();
    } catch (_) {
      toast.error("Erro ao registrar check-in.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="checkin-page">
      <div>
        <div className="overline text-accent mb-3">Check-in semanal</div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">Como foi sua semana?</h1>
        <p className="text-muted-foreground mt-2 max-w-2xl">
          Avalie sua evolução clínica. Suas respostas alimentam seu score e permitem detectar pioras precocemente.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Form */}
        <div className="lg:col-span-2 bg-card border border-border rounded-sm p-8">
          <div className="space-y-8">
            {questions.map((q) => (
              <div key={q.key}>
                <div className="flex justify-between items-end mb-1">
                  <div>
                    <div className="font-heading font-semibold">{q.label}</div>
                    {q.description && <div className="text-xs text-muted-foreground">{q.description}</div>}
                  </div>
                  <div className="font-heading text-2xl font-bold text-primary" data-testid={`checkin-${q.key}-value`}>{answers[q.key] ?? 0}</div>
                </div>
                <input
                  type="range" min={q.min ?? 0} max={q.max ?? 10} value={answers[q.key] ?? 0}
                  onChange={(e) => setAnswers({ ...answers, [q.key]: Number(e.target.value) })}
                  className="w-full accent-primary mt-2"
                  data-testid={`checkin-${q.key}-input`}
                />
              </div>
            ))}
            <div>
              <div className="font-heading font-semibold mb-1">Efeitos colaterais ou observações</div>
              <textarea
                value={sideEffects}
                onChange={(e) => setSideEffects(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="Descreva qualquer desconforto ou mudança…"
                data-testid="checkin-sideeffects-input"
              />
            </div>
            <button
              onClick={submit} disabled={submitting}
              className="w-full rounded-sm bg-primary text-primary-foreground px-4 py-3 text-sm font-semibold hover:bg-primary/90 disabled:opacity-60 transition-colors"
              data-testid="checkin-submit-btn"
            >
              {submitting ? "Enviando…" : "Registrar check-in"}
            </button>
            {lastResult && (
              <div className="rounded-sm border border-success/30 bg-success/5 p-4 text-sm" data-testid="checkin-result">
                Check-in registrado com score <b>{lastResult.score}</b>.
              </div>
            )}
          </div>
        </div>

        {/* History */}
        <div className="bg-card border border-border rounded-sm p-6">
          <div className="overline text-muted-foreground mb-3">Histórico de scores</div>
          <div className="h-48 mb-4">
            {history.length > 0 ? (
              <ResponsiveContainer>
                <LineChart data={history.map((h) => ({ date: h.created_at.slice(5, 10), score: h.score }))}>
                  <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={10} />
                  <YAxis domain={[0, 100]} stroke="hsl(var(--muted-foreground))" fontSize={10} />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 4, fontSize: 12 }} />
                  <Line type="monotone" dataKey="score" stroke="hsl(var(--primary))" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full grid place-items-center text-xs text-muted-foreground">Nenhum check-in ainda.</div>
            )}
          </div>
          <div className="space-y-2 max-h-60 overflow-auto">
            {history.slice().reverse().slice(0, 6).map((h) => (
              <div key={h.id} className="flex items-center justify-between text-sm border-b border-border last:border-0 py-2">
                <span className="text-muted-foreground">{new Date(h.created_at).toLocaleDateString("pt-BR")}</span>
                <span className="font-semibold text-primary">{h.score}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
